<template>
    <div
        class="slider-toggle"
        :data-test-id="`${dataTestId}-container`"
        :class="{ active: value, locked: isLocked, disabled }"
    >
        <span
            class="switch slider-toggle__switch"
            :data-test-id="`${dataTestId}-toggle`"
            @click="isLocked && $emit('lockClicked')"
        >
            <button
                type="button"
                role="switch"
                class="slider round"
                :aria-checked="value"
                :aria-labelledby="labelledBy"
                :aria-label="labelledBy ? null : accessibleName"
                :aria-describedby="description ? descriptionId : null"
                :disabled="disabled"
                :data-focus-key="focusKey || null"
                :data-test-id="`${dataTestId}-toggle-click-target`"
                @click.prevent="!disabled && $emit('update', !value)"
            />
        </span>
        <span class="text-container slider-toggle__label">
            <div class="text-container-header">
                <h1
                    :id="labelId"
                    :data-test-id="`${dataTestId}-label`"
                    :class="{ 'has-tooltip': tooltip }"
                >
                    {{ label }}
                </h1>
                <div
                    v-if="tooltip"
                    class="slider-tooltip-icon"
                    :data-test-id="`${dataTestId}-tooltip-toggle`"
                    @click="tooltipIconClicked"
                >
                    <span
                        :id="tooltipId"
                        v-click-outside="tooltipDismissed"
                        class="slider-tooltip-text"
                        :data-test-id="`${dataTestId}-slider-tooltip-text`"
                        :class="{ active: isTooltipVisible }"
                        @click.stop="() => {}"
                    >
                        {{ tooltip }}
                    </span>
                </div>
            </div>
            <p
                v-if="description"
                :id="descriptionId"
                :data-test-id="`${dataTestId}-description`"
            >
                {{ description }}
            </p>
        </span>
    </div>
</template>

<script>
import { getCurrentInstance } from 'vue'

export default {
    props: {
        name: {
            type: String,
            default: () => 'name',
        },
        label: {
            type: String,
            default: () => 'Label',
        },
        // For the call sites that render their own visible text and pass
        // label="". Without it the switch has no accessible name at all
        // (SC 4.1.2), which is worse than the old unreachable <span>.
        ariaLabel: {
            type: String,
            default: () => '',
        },
        description: {
            type: String,
            default: () => '',
        },
        value: {
            type: Boolean,
            default: () => false,
        },
        isLocked: {
            type: Boolean,
            default: () => false,
        },
        tooltip: {
            type: String,
            default: () => '',
        },
        disabled: {
            type: Boolean,
            default: () => false,
        },
        dataTestId: {
            type: String,
            default: () => '',
        },
        // Runtime handle for pages that move focus to this control after an
        // async write. Deliberately not the data-test-id: test ids must stay
        // free to change without breaking focus management.
        focusKey: {
            type: String,
            default: () => '',
        },
    },

    data() {
        return {
            isTooltipVisible: false,
        }
    },

    computed: {
        // An empty label renders an empty <h1>, so pointing aria-labelledby at
        // it would name the switch nothing. Fall back to aria-label instead.
        labelledBy() {
            return this.label ? this.labelId : null
        },

        accessibleName() {
            return this.ariaLabel || this.name
        },

        tooltipId() {
            return `${this.uid}-slider-tooltip-text`
        },
        // Leading letter on purpose: uid is a number, and an id starting
        // with a digit is legal HTML but an invalid CSS selector, which
        // breaks anything that tries to query it.
        labelId() {
            return `slider-toggle-${this.uid}-label`
        },
        descriptionId() {
            return `slider-toggle-${this.uid}-description`
        },
        uid() {
            return getCurrentInstance().uid
        },
    },

    created() {
        if (this.tooltip && this.tooltip.length > 0) {
            this.$eventBus.on('slider-tooltip-clicked', (id) => {
                if (id !== this.tooltipId) {
                    this.isTooltipVisible = false
                }
            })
        }
    },

    unmounted() {
        this.$eventBus.off('slider-tooltip-clicked')
    },

    methods: {
        tooltipIconClicked(e) {
            this.$eventBus.emit('slider-tooltip-clicked', this.tooltipId)
            if (!this.isTooltipVisible) {
                e.stopPropagation()
            }
            this.isTooltipVisible = !this.isTooltipVisible
        },
        tooltipDismissed(e) {
            this.isTooltipVisible = false
        },
    },
}
</script>

<style scoped lang="scss">
@use '@sass/_variables.scss' as *;

.text-container {
    display: flex;
    flex-direction: column;
    justify-content: center;

    .text-container-header {
        display: flex;
        flex-direction: row;
        gap: 0.5rem;
    }
}

.slider-toggle.active .slider-toggle__label h1 {
    color: #fff;
}

.slider-toggle {
    &.disabled {
        cursor: not-allowed;
        .slider-toggle__label,
        .slider-toggle__switch .slider {
            cursor: not-allowed;
        }
    }
}

.slider-toggle__label {
    position: relative;
    width: auto;
    font-family: 'Avenir Book';
    cursor: default;

    h1 {
        font-size: 14.6px;
        line-height: 20px;
        color: white;
    }
    p {
        font-size: 12px;
        line-height: 16px;

        // The wrapper used to be a <label>, so inside a form the description
        // inherited `form label { color: $text-muted }`. The wrapper is no
        // longer a label (it labels nothing), so state the colour here rather
        // than turning every description in the app white.
        color: $text-muted;
    }
    .slider-tooltip-icon {
        width: 17px;
        height: 17px;
        background-image: url(@images/icon-info.svg);
        background-size: 17px 17px;
        background-repeat: no-repeat;
        background-position: top left;
        content: '';
        display: block;
        cursor: pointer;
        position: relative;
        .slider-tooltip-text {
            display: none;
            width: 220px;
            background: #212628;
            border-radius: 4px;
            color: white;
            font-size: 12px;
            line-height: 16.39px;
            font-family: 'Avenir Light';
            padding: 10px;
            text-align: center;
            position: absolute;
            top: 25px;
            left: -102px;
            opacity: 95%;
            z-index: 9999;
            cursor: default;
            &.active {
                display: block;
            }
        }
    }
}
</style>
